function isEscapedCharacter(value: string, index: number): boolean {
  let precedingBackslashes = 0;

  for (
    let currentIndex = index - 1;
    currentIndex >= 0 && value[currentIndex] === "\\";
    currentIndex -= 1
  ) {
    precedingBackslashes += 1;
  }

  return precedingBackslashes % 2 === 1;
}

function getMarkdownTableCells(line: string): string[] {
  const trimmedLine = line.trim();
  const withoutLeadingPipe = trimmedLine.startsWith("|")
    ? trimmedLine.slice(1)
    : trimmedLine;
  const lastCharacterIndex = withoutLeadingPipe.length - 1;
  const withoutOuterPipes =
    withoutLeadingPipe.endsWith("|") &&
    !isEscapedCharacter(withoutLeadingPipe, lastCharacterIndex)
    ? withoutLeadingPipe.slice(0, -1)
    : withoutLeadingPipe;
  const cells: string[] = [];
  let currentCell = "";

  for (let index = 0; index < withoutOuterPipes.length; index += 1) {
    const character = withoutOuterPipes[index];

    if (
      character === "|" &&
      !isEscapedCharacter(withoutOuterPipes, index)
    ) {
      cells.push(currentCell.trim());
      currentCell = "";
      continue;
    }

    currentCell += character;
  }

  cells.push(currentCell.trim());
  return cells;
}

function isMarkdownTableRow(line: string): boolean {
  return line.trim() !== "" && line.includes("|");
}

function isMarkdownTableDelimiterRow(line: string): boolean {
  if (!isMarkdownTableRow(line)) {
    return false;
  }

  const cells = getMarkdownTableCells(line);
  return (
    cells.length > 0 &&
    cells.every((cell) => /^:?-{3,}:?$/.test(cell))
  );
}

function getFencedCodeLineMask(lines: string[]): boolean[] {
  const mask = new Array<boolean>(lines.length).fill(false);
  let fenceCharacter: "`" | "~" | null = null;
  let fenceLength = 0;

  lines.forEach((line, index) => {
    const fenceMatch = line.trimStart().match(/^(`{3,}|~{3,})/);

    if (fenceCharacter === null) {
      if (fenceMatch) {
        const marker = fenceMatch[1];
        fenceCharacter = marker[0] as "`" | "~";
        fenceLength = marker.length;
        mask[index] = true;
      }
      return;
    }

    mask[index] = true;
    if (
      fenceMatch &&
      fenceMatch[1][0] === fenceCharacter &&
      fenceMatch[1].length >= fenceLength
    ) {
      fenceCharacter = null;
      fenceLength = 0;
    }
  });

  return mask;
}

function findNextNonBlankLineIndex(lines: string[], startIndex: number): number {
  for (let index = startIndex; index < lines.length; index += 1) {
    if (lines[index].trim() !== "") {
      return index;
    }
  }

  return -1;
}

export function normalizeMarkdownTableSpacing(content: string): string {
  const lines = content.split("\n");
  const fencedCodeLines = getFencedCodeLineMask(lines);
  const normalizedLines: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const headerLine = lines[index];

    if (fencedCodeLines[index] || !isMarkdownTableRow(headerLine)) {
      normalizedLines.push(headerLine);
      index += 1;
      continue;
    }

    const delimiterIndex = findNextNonBlankLineIndex(lines, index + 1);
    const headerCellCount = getMarkdownTableCells(headerLine).length;
    const hasMatchingDelimiter =
      delimiterIndex !== -1 &&
      !fencedCodeLines[delimiterIndex] &&
      isMarkdownTableDelimiterRow(lines[delimiterIndex]) &&
      getMarkdownTableCells(lines[delimiterIndex]).length === headerCellCount;

    if (!hasMatchingDelimiter) {
      normalizedLines.push(headerLine);
      index += 1;
      continue;
    }

    normalizedLines.push(headerLine, lines[delimiterIndex]);
    index = delimiterIndex + 1;

    while (index < lines.length) {
      const currentLine = lines[index];

      if (fencedCodeLines[index]) {
        break;
      }

      if (currentLine.trim() !== "") {
        if (!isMarkdownTableRow(currentLine)) {
          break;
        }

        normalizedLines.push(currentLine);
        index += 1;
        continue;
      }

      const nextRowIndex = findNextNonBlankLineIndex(lines, index + 1);
      const possibleNextDelimiterIndex =
        nextRowIndex === -1
          ? -1
          : findNextNonBlankLineIndex(lines, nextRowIndex + 1);
      const startsAnotherTable =
        nextRowIndex !== -1 &&
        possibleNextDelimiterIndex !== -1 &&
        !fencedCodeLines[possibleNextDelimiterIndex] &&
        isMarkdownTableDelimiterRow(lines[possibleNextDelimiterIndex]) &&
        getMarkdownTableCells(lines[possibleNextDelimiterIndex]).length ===
          getMarkdownTableCells(lines[nextRowIndex]).length;
      const canCollapseBlankLines =
        nextRowIndex !== -1 &&
        !fencedCodeLines[nextRowIndex] &&
        isMarkdownTableRow(lines[nextRowIndex]) &&
        getMarkdownTableCells(lines[nextRowIndex]).length === headerCellCount &&
        !startsAnotherTable;

      if (!canCollapseBlankLines) {
        break;
      }

      normalizedLines.push(lines[nextRowIndex]);
      index = nextRowIndex + 1;
    }
  }

  return normalizedLines.join("\n");
}

export function containsMarkdownTable(content: string): boolean {
  const lines = content.split("\n");
  const fencedCodeLines = getFencedCodeLineMask(lines);

  for (let index = 0; index < lines.length - 1; index += 1) {
    if (
      fencedCodeLines[index] ||
      fencedCodeLines[index + 1] ||
      !isMarkdownTableRow(lines[index]) ||
      !isMarkdownTableDelimiterRow(lines[index + 1])
    ) {
      continue;
    }

    if (
      getMarkdownTableCells(lines[index]).length ===
      getMarkdownTableCells(lines[index + 1]).length
    ) {
      return true;
    }
  }

  return false;
}

export function isPotentialMarkdownTable(content: string): boolean {
  if (containsMarkdownTable(content)) {
    return true;
  }

  const lines = content
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  const lastLine = lines[lines.length - 1];

  return Boolean(lastLine && isMarkdownTableRow(lastLine));
}
